import gql from 'graphql-tag';
import { differenceBy, findIndex, get, isArray, isFunction, pick } from 'lodash';

import { defaults as allDefaults } from 'model';
import {
	useCreateIndicatorMutation,
	useUpdateIndicatorMutation,
	useUpdateIndicatorScenarioMutation,
	useDeleteIndicatorMutation,
	useCreateTaggingMutation,
	useDeleteTaggingMutation,
} from 'graph/dist/generated';
import {
	getFieldFunctions,
	getFragmentFields,
	optimisticlyUpdateScenario,
	hideElementInOtherScenarios
} from 'utils/graphql';

export const useIndicatorController = () => {
	const [createIndicator] = useCreateIndicatorMutation();
	const [updateIndicator] = useUpdateIndicatorMutation();
	const [updateIndicatorScenario] = useUpdateIndicatorScenarioMutation();
	const [deleteIndicator] = useDeleteIndicatorMutation();
	const [createTagging] = useCreateTaggingMutation();
	const [deleteTagging] = useDeleteTaggingMutation();

	const updateTaggings = async (indicator: any, tagIds: string[]) => {
		if (!tagIds || !isArray(tagIds)) return;
		const oldTaggings = indicator.taggings || [];
		const newTaggings = tagIds.map((tagId) => ({ tagId, objectId: indicator.id }));
		const toDelete = differenceBy(oldTaggings, newTaggings, 'tagId');
		const toAdd = differenceBy(newTaggings, oldTaggings, 'tagId');
		for (const tagging of toDelete) {
			await deleteTagging({
				variables: { id: tagging.id },
				update(cache, { data: { deleteTagging } }: any) {
					cache.modify({
						id: `Indicator:${tagging.objectId}`,
						fields: {
							taggings: (taggings = []) =>
								taggings.filter((t: any) => t.__ref !== `Tagging:${deleteTagging.id}`)
						}
					});
				}
			});
		}
		for (const tagging of toAdd) {
			const variables = { projectId: indicator.projectId, ...tagging, type: 'indicator' };
			await createTagging({
				variables,
				update(cache, { data: { createTagging } }: any) {
					cache.modify({
						id: `Indicator:${tagging.objectId}`,
						fields: {
							taggings: (taggings = []) => {
								const newRef = cache.writeFragment({
									id: 'Tagging:' + createTagging.id,
									data: createTagging,
									fragment: gql`fragment TF on Tagging { ${getFragmentFields(variables)} }`
								});
								return [...taggings, newRef];
							}
						}
					});
				}
			});
		}
	};

	const handleCreateIndicator = async (
		projectId: string,
		values: Object,
		defaults = {},
		scenario = null,
		scenarios: any[] = []
	) => {
		let appliedDefaults = {
			...allDefaults.Indicator,
			...defaults
		};

		let variables = {
			projectId: projectId,
			name: values.name,
			weight: values.weight || appliedDefaults.weight,
			trend:
				values.trend !== null && values.trend !== undefined
					? values.trend
					: appliedDefaults.trend,
			description: values.description || appliedDefaults.description,
			reference: values.reference || appliedDefaults.reference,
			indicatorCategoryId: values.indicatorCategoryId || null,
			evaluatedAt: values.evaluatedAt || appliedDefaults.evaluatedAt,
			frequency: values.frequency || null,
			// Si on est dans un scénario, créer directement désactivé dans la baseline
			enabled: scenario ? false : true
		};

		let result = await createIndicator({
			variables,
			update(cache, { data: { createIndicator } }) {
				cache.modify({
					id: `Project:${projectId}`,
					fields: {
						indicators(indicators = []) {
							const newIndicator = cache.writeFragment({
								id: 'Indicator:' + createIndicator.id,
								data: createIndicator,
								fragment: gql`
									fragment ConstraintFragment on Indicator {
										${getFragmentFields(variables)}
									}
								`
							});

							return [...indicators, newIndicator];
						}
					}
				});
			},
			optimisticResponse: {
				createIndicator: {
					id: 'temp-id',
					...variables,
					createdAt: new Date().toISOString(),
					scenarios: []
				}
			}
		});
		
		// Si un scénario est actif, associer les valeurs au scénario (avec enabled: true)
		if (scenario && result.data?.createIndicator?.id) {
			const indicator = result.data.createIndicator;
			
			const scenarioValues = {
				trend: variables.trend || '50',
				weight: variables.weight || 1,
				enabled: true
			};
			
			await optimisticlyUpdateScenario(
				indicator,
				'Indicator',
				scenario.id,
				scenarioValues,
				updateIndicatorScenario
			);
		}

		// Masquer l'élément dans tous les autres scénarios
		if (result.data?.createIndicator?.id && scenarios.length > 0) {
			await hideElementInOtherScenarios(
				result.data.createIndicator,
				'Indicator',
				scenarios,
				scenario,
				updateIndicatorScenario
			);
		}
		
		return result;
	};

	const handleUpdateIndicator = async (
		id,
		indicators,
		scenario,
		values
	) => {
		let index = findIndex(indicators, (s: any) => s.id === id);

		// Champs qui sont toujours stockés dans la baseline (non modifiables par scénario)
		const baselineFields = ['name', 'reference', 'description', 'evaluatedAt', 'frequency', 'indicatorCategoryId'];

		// Champs qui peuvent être modifiés par scénario
		const scenarioFields = ['trend', 'weight', 'enabled'];

		// Vérifie si on a des champs baseline à mettre à jour
		const baselineValues = pick(values, baselineFields);
		const hasBaselineChanges = Object.keys(baselineValues).length > 0;

		// Vérifie si on a des champs scénario à mettre à jour
		const scenarioValuesFromForm = pick(values, scenarioFields);
		const hasScenarioChanges = Object.keys(scenarioValuesFromForm).length > 0;

		// Mise à jour des champs baseline (toujours, même si un scénario est actif)
		if (hasBaselineChanges) {
			let newIndicator = {
				...pick(indicators[index], Object.keys(baselineValues)),
				...baselineValues
			};

			if (values.evaluatedAt !== undefined)
				newIndicator.evaluatedAt =
					newIndicator.evaluatedAt === null ||
					!isFunction(newIndicator.evaluatedAt?.toISOString)
						? ''
						: newIndicator.evaluatedAt.toISOString();

			await updateIndicator({
				variables: {
					id: id,
					...newIndicator
				},
				update(cache) {
					cache.modify({
						id: `Indicator:${id}`,
						fields: getFieldFunctions(baselineValues)
					});
				},
				optimisticResponse: {
					updateIndicator: true
				}
			});
		}

		// Mise à jour des champs scénario
		if (hasScenarioChanges) {
		if (scenario) {
			// Si un scénario est actif, envoyer uniquement les champs modifiés
			await optimisticlyUpdateScenario(
				indicators[index],
				'Indicator',
				scenario.id,
				scenarioValuesFromForm,
				updateIndicatorScenario
			);
			} else {
				// Si pas de scénario, mettre à jour les valeurs baseline
				await updateIndicator({
					variables: {
						id: id,
						...scenarioValuesFromForm
					},
					update(cache) {
						cache.modify({
							id: `Indicator:${id}`,
							fields: getFieldFunctions(scenarioValuesFromForm)
						});
					},
					optimisticResponse: {
						updateIndicator: true
					}
				});
			}
		}
	};

	const handleDelete = async (projectId: string, id: string | undefined) => {
		if (id == null) {
			return;
		}

		await deleteIndicator({
			variables: { id: id },
			update(cache) {
				cache.modify({
					id: `Project:${projectId}`,
					fields: {
						indicators(existingConstraintsRef, { readField }) {
							return existingConstraintsRef.filter(
								(ref: string) => id !== readField('id', ref)
							);
						}
					}
				});
			},

			optimisticResponse: {
				deleteIndicator: true
			}
		});
	};

	return {
		createIndicator: handleCreateIndicator,
		updateIndicator: handleUpdateIndicator,
		updateIndicatorTaggings: updateTaggings,
		deleteIndicator: handleDelete
	};
};
